import type React from 'react'
import { useState } from 'react'
import { API_HOST, authenticatedFetchData, emailRegex } from '../../../../lib'
import { logError } from '@tomra/datadog-browser-logging'
import { Dialog } from '../../../common/Dialog'
import { Button } from '@tomra/design-system-react/button'

type Props = {
  userId: string
  onClose: () => void
}

export const UpdateEmailDialog = ({ userId, onClose }: Props) => {
  const [status, setStatus] = useState<RequestStatusType | 'validation-failed' | 'email-in-use'>('idle')

  const updateEmail = async (evt: React.FormEvent<HTMLFormElement>) => {
    evt.preventDefault()
    setStatus('pending')

    try {
      const formData = new FormData(evt.target as HTMLFormElement)
      const newEmail = formData.get('newEmail')?.toString() || ''
      const isValidEmail = !!new RegExp(emailRegex).exec(newEmail)

      if (!isValidEmail) {
        setStatus('validation-failed')
        return
      }

      await authenticatedFetchData(
        `${API_HOST}/mytomra/v1.0/hotlinetool/update-user-email?userId=${encodeURIComponent(userId)}&email=${encodeURIComponent(newEmail)}`,
        {
          method: 'POST'
        }
      ).run()
      setStatus('success')
    } catch (error: any) {
      if (error.status === 409) setStatus('email-in-use')
      else setStatus('failure')
      logError(new Error('Failed to set password login on user'), error)
    }
  }

  return (
    <>
      <Dialog ariaLabel={`Changes ${status}`} onClose={onClose}>
        <div className="rounded-2xl bg-white shadow-md dark:bg-gray-900 p-6 flex flex-col gap-y-4 pt-9 max-w-300">
          <h1 className="text-xl">Update user email</h1>
          <p>
            The email wont be updated until the user completes verification by clicking on the verification link sent to
            the users new email address.
          </p>

          <form onSubmit={updateEmail} className="flex flex-col gap-y-4">
            <label htmlFor="newEmail">New email</label>
            <input
              type="email"
              id="newEmail"
              name="newEmail"
              placeholder="New email"
              autoComplete="email"
              title="Must be a valid email address"
              className="mb-2 border border-gray-300 rounded px-3 py-2 bg-white w-full focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
              required
            />

            {status === 'success' ? (
              <div className="rounded-lg border-l-[3px] border-emerald-500 bg-white p-4 text-left shadow-md dark:bg-gray-900">
                Success! A verification email has been sent to the user.
              </div>
            ) : status === 'failure' ? (
              <div className="rounded-lg border-l-[3px] border-rose-500 bg-white p-4 text-left shadow-md dark:bg-gray-900">
                Whoopsie! Something went wrong.
              </div>
            ) : status === 'validation-failed' ? (
              <div className="rounded-lg border-l-[3px] border-amber-400 bg-white p-4 text-left shadow-md dark:bg-gray-900">
                Must be a valid email address.
              </div>
            ) : status === 'email-in-use' ? (
              <div className="rounded-lg border-l-[3px] border-rose-500 bg-white p-4 text-left shadow-md dark:bg-gray-900">
                New email is already in use.
              </div>
            ) : null}

            <Button
              type="submit"
              variant="primary"
              className="ml-auto"
              disabled={status === 'pending' || status === 'success'}
              aria-disabled={status === 'pending' || status === 'success'}
            >
              {status === 'pending' ? <div className="loadingSpinner" /> : 'Confirm'}
            </Button>
          </form>
        </div>
      </Dialog>
    </>
  )
}
